// Curated Google Business Profile attributes. Operators search these by name
// constantly — each gets its own indexable page.

export interface GbpAttribute {
  slug: string;
  name: string;
  group:
    | "Accessibility"
    | "Service options"
    | "Dining options"
    | "Amenities"
    | "Payments"
    | "Health & safety"
    | "Crowd"
    | "Planning"
    | "Atmosphere"
    | "Offerings"
    | "Highlights"
    | "From the business";
  description: string;
  /** Who can set this (merchant vs. user-contributed vs. inferred). */
  setBy: "Merchant" | "User-contributed" | "Merchant + user-contributed" | "Inferred";
  /** Which category families typically expose this attribute. */
  appearsFor: string[];
  /** How it surfaces on the Map Pack / Maps listing. */
  surfacesAs: string;
}

export const GBP_ATTRIBUTES: GbpAttribute[] = [
  {
    slug: "wheelchair-accessible-entrance",
    name: "Wheelchair-accessible entrance",
    group: "Accessibility",
    description:
      "Indicates the business has an entrance usable by people in wheelchairs — no steps, doorway wide enough, level or ramped approach.",
    setBy: "Merchant + user-contributed",
    appearsFor: ["Restaurants", "Retail", "Health & Medical", "Lodging", "Most physical venues"],
    surfacesAs:
      "Accessibility icon on the business profile and a dedicated row in the 'Accessibility' panel. Used by Google's accessibility filter in Maps.",
  },
  {
    slug: "wheelchair-accessible-restroom",
    name: "Wheelchair-accessible restroom",
    group: "Accessibility",
    description: "Restroom on the premises is usable by people in wheelchairs.",
    setBy: "Merchant + user-contributed",
    appearsFor: ["Restaurants", "Retail", "Lodging"],
    surfacesAs: "Listed in the Accessibility panel.",
  },
  {
    slug: "wheelchair-accessible-parking-lot",
    name: "Wheelchair-accessible parking lot",
    group: "Accessibility",
    description: "Designated accessible parking spaces are available.",
    setBy: "Merchant + user-contributed",
    appearsFor: ["Most physical venues with a lot"],
    surfacesAs: "Listed in the Accessibility panel.",
  },
  {
    slug: "dine-in",
    name: "Dine-in",
    group: "Service options",
    description: "Customers can eat on-premises.",
    setBy: "Merchant",
    appearsFor: ["Restaurant", "Cafe", "Bar", "Bakery"],
    surfacesAs: "'Service options' line on the listing and a justification chip ('Dine-in') in the local pack.",
  },
  {
    slug: "takeout",
    name: "Takeout",
    group: "Service options",
    description: "Customers can order food to take away.",
    setBy: "Merchant",
    appearsFor: ["Restaurant", "Cafe", "Bakery", "Grocery Store"],
    surfacesAs: "Service options line + 'Takeout' justification chip.",
  },
  {
    slug: "delivery",
    name: "Delivery",
    group: "Service options",
    description: "Business delivers orders to the customer.",
    setBy: "Merchant",
    appearsFor: ["Restaurants", "Grocery Store", "Pharmacy", "Florist"],
    surfacesAs: "Service options line + 'Delivery' chip in the local pack.",
  },
  {
    slug: "curbside-pickup",
    name: "Curbside pickup",
    group: "Service options",
    description: "Customers can collect orders from the kerb without entering the venue.",
    setBy: "Merchant",
    appearsFor: ["Restaurants", "Retail", "Grocery Store"],
    surfacesAs: "'Curbside pickup' chip and inclusion in the Maps 'Curbside' filter.",
  },
  {
    slug: "no-contact-delivery",
    name: "No-contact delivery",
    group: "Service options",
    description: "Delivery can be left without direct contact between driver and customer.",
    setBy: "Merchant",
    appearsFor: ["Restaurants", "Grocery", "Pharmacy"],
    surfacesAs: "Highlighted as a service option, especially in regions with active health advisories.",
  },
  {
    slug: "online-appointments",
    name: "Online appointments",
    group: "Planning",
    description: "Customers can book appointments online.",
    setBy: "Merchant",
    appearsFor: ["Health & Medical", "Beauty & Wellness", "Professional Services"],
    surfacesAs: "'Book online' or 'Schedule' button on the listing, often linked via Reserve with Google partners.",
  },
  {
    slug: "appointments-recommended",
    name: "Appointments recommended",
    group: "Planning",
    description: "Walk-ins accepted but appointments are preferred.",
    setBy: "Merchant",
    appearsFor: ["Salons", "Doctors", "Mechanics"],
    surfacesAs: "Note under planning on the listing.",
  },
  {
    slug: "free-wifi",
    name: "Free Wi-Fi",
    group: "Amenities",
    description: "Free Wi-Fi is available on-premises.",
    setBy: "Merchant + user-contributed",
    appearsFor: ["Cafes", "Hotels", "Restaurants"],
    surfacesAs: "Amenities chip.",
  },
  {
    slug: "free-parking-lot",
    name: "Free parking lot",
    group: "Amenities",
    description: "On-site parking available at no cost.",
    setBy: "Merchant + user-contributed",
    appearsFor: ["Most physical venues with a lot"],
    surfacesAs: "Amenities chip.",
  },
  {
    slug: "outdoor-seating",
    name: "Outdoor seating",
    group: "Dining options",
    description: "Seating is available outdoors — patio, terrace, sidewalk.",
    setBy: "Merchant + user-contributed",
    appearsFor: ["Restaurant", "Bar", "Cafe"],
    surfacesAs: "Dining options chip; weights heavily for warm-weather queries.",
  },
  {
    slug: "good-for-working-on-a-laptop",
    name: "Good for working on a laptop",
    group: "Atmosphere",
    description:
      "User-contributed signal that the venue is suitable for laptop work — power outlets, Wi-Fi, room to spread out.",
    setBy: "User-contributed",
    appearsFor: ["Cafes", "Coffee Shops", "Hotels"],
    surfacesAs: "Atmosphere chip on the listing; influences 'cafe with Wi-Fi' style searches.",
  },
  {
    slug: "lgbtq-friendly",
    name: "LGBTQ+ friendly",
    group: "From the business",
    description: "Merchant attestation that the business is welcoming to LGBTQ+ customers.",
    setBy: "Merchant",
    appearsFor: ["All categories"],
    surfacesAs: "Highlights section on the listing.",
  },
  {
    slug: "transgender-safespace",
    name: "Transgender safespace",
    group: "From the business",
    description: "Merchant attestation that the business is a safe space for transgender customers.",
    setBy: "Merchant",
    appearsFor: ["All categories"],
    surfacesAs: "Highlights section on the listing.",
  },
  {
    slug: "identifies-as-women-owned",
    name: "Identifies as women-owned",
    group: "From the business",
    description: "Merchant attestation that the business is women-owned.",
    setBy: "Merchant",
    appearsFor: ["All categories"],
    surfacesAs: "Surfaced under 'From the business' with an icon.",
  },
  {
    slug: "identifies-as-veteran-owned",
    name: "Identifies as veteran-owned",
    group: "From the business",
    description: "Merchant attestation that the business is veteran-owned (US-focused).",
    setBy: "Merchant",
    appearsFor: ["All categories"],
    surfacesAs: "'From the business' icon.",
  },
  {
    slug: "identifies-as-black-owned",
    name: "Identifies as Black-owned",
    group: "From the business",
    description: "Merchant attestation that the business is Black-owned.",
    setBy: "Merchant",
    appearsFor: ["All categories"],
    surfacesAs: "'From the business' icon; used by Google's 'Black-owned' filter in Maps.",
  },
  {
    slug: "identifies-as-latino-owned",
    name: "Identifies as Latino-owned",
    group: "From the business",
    description: "Merchant attestation that the business is Latino-owned.",
    setBy: "Merchant",
    appearsFor: ["All categories"],
    surfacesAs: "'From the business' icon.",
  },
  {
    slug: "identifies-as-asian-owned",
    name: "Identifies as Asian-owned",
    group: "From the business",
    description: "Merchant attestation that the business is Asian-owned.",
    setBy: "Merchant",
    appearsFor: ["All categories"],
    surfacesAs: "'From the business' icon.",
  },
  {
    slug: "online-estimates",
    name: "Online estimates",
    group: "Service options",
    description: "Customers can request a price estimate online without an on-site visit.",
    setBy: "Merchant",
    appearsFor: ["Home Services", "Automotive", "Plumber", "Electrician", "Roofing"],
    surfacesAs: "Service options chip; helps for 'free quote' / 'free estimate' queries.",
  },
  {
    slug: "onsite-services",
    name: "Onsite services",
    group: "Service options",
    description: "Business performs services at the customer's premises.",
    setBy: "Merchant",
    appearsFor: ["Home Services", "Auto Repair", "IT Services"],
    surfacesAs: "Service options chip — important for service-area businesses.",
  },
  {
    slug: "credit-cards",
    name: "Credit cards",
    group: "Payments",
    description: "Business accepts credit card payments.",
    setBy: "Merchant + user-contributed",
    appearsFor: ["Most venues"],
    surfacesAs: "Payments chip.",
  },
  {
    slug: "nfc-mobile-payments",
    name: "NFC mobile payments",
    group: "Payments",
    description: "Business accepts contactless mobile payments (Apple Pay, Google Pay).",
    setBy: "Merchant + user-contributed",
    appearsFor: ["Retail", "Restaurants", "Cafes"],
    surfacesAs: "Payments chip.",
  },
];

export function getAttributeBySlug(slug: string): GbpAttribute | undefined {
  return GBP_ATTRIBUTES.find((a) => a.slug === slug);
}

export function listAttributeGroups(): string[] {
  return Array.from(new Set(GBP_ATTRIBUTES.map((a) => a.group))).sort();
}
